import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ButtonIcon, Tooltip, TooltipTrigger } from '@backstage/ui';
import { sidebarConfig, useSidebarPinState } from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  routeResolutionApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { devToolsPlugin } from '@backstage/plugin-devtools';
import {
  notificationsPlugin,
  useNotificationsApi,
} from '@backstage/plugin-notifications';
import { userSettingsPlugin } from '@backstage/plugin-user-settings';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { adminReadPermission } from '@internal/backstage-plugin-catalog-devops-common';
import type { ClusterEntity } from '@internal/backstage-plugin-catalog-devops-common';
import { useAccountsContext } from '@internal/backstage-plugin-catalog-devops';
import { useProjectsContext } from '@internal/backstage-plugin-devops-admin';
import {
  BellIcon,
  BookIcon,
  DomainIcon,
  BoxesIcon,
  GearIcon,
  HomeIcon,
  LogoIcon,
  PanelLeftIcon,
  SearchIcon,
  ShieldIcon,
  TerminalIcon,
} from './Icons';
import { entityHref } from './ProviderBadge';
import { ProjectsSection } from './ProjectsSection';
import { QuickSwitcher, SwitcherTarget } from './QuickSwitcher';
import { UserMenu } from './UserMenu';
import styles from './Sidebar.module.css';

/*
 * The custom sidebar shell is 264px expanded / 64px rail (per
 * design/sidebar-menu-proposal.md). plugin-app's layout wraps this nav in
 * SidebarPage from core-components, which pads the page content by
 * sidebarConfig widths — keep the shared config in sync with the shell.
 * The classic MUI Sidebar no longer renders anywhere in this app, so
 * nothing else consumes these values.
 */
sidebarConfig.drawerWidthOpen = 264;
sidebarConfig.drawerWidthClosed = 64;

const HELP_HREF = '/docs/default/component/backstage-documentation';
const ADMIN_HREF = '/admin';

function isSamePage(pathname: string, href: string): boolean {
  const normalized = href.replace(/\/+$/, '') || '/';
  if (normalized === '/') {
    return pathname === '/';
  }
  return pathname === normalized || pathname.startsWith(`${normalized}/`);
}

function NavItemLink(props: {
  href: string;
  icon: ReactNode;
  label: string;
  active: boolean;
  end?: ReactNode;
  tooltip?: string;
}) {
  const { href, icon, label, active, end, tooltip } = props;
  const row = (
    <Link
      to={href}
      className={styles.navItem}
      aria-current={active ? 'page' : undefined}
    >
      {icon}
      <span className={styles.lbl}>{label}</span>
      {end && <span className={styles.end}>{end}</span>}
    </Link>
  );
  // Rail tooltips only — in the expanded menu the label is already visible
  // (mirrors the mockup, where data-tip is a rail-only affordance).
  if (!tooltip) {
    return row;
  }
  return (
    <TooltipTrigger>
      {row}
      <Tooltip placement="right">{tooltip}</Tooltip>
    </TooltipTrigger>
  );
}

/** The whole left navigation: header, search, primary nav, projects, footer. */
function Sidebar() {
  const { isPinned, isMobile, toggleSidebarPinState } = useSidebarPinState();
  const { pathname } = useLocation();
  const routeResolutionApi = useApi(routeResolutionApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { projects } = useProjectsContext();
  const { accounts } = useAccountsContext();

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [clusters, setClusters] = useState<ClusterEntity[]>([]);
  const navRef = useRef<HTMLElement>(null);

  const admin = usePermission({ permission: adminReadPermission });

  const notificationsStatus = useNotificationsApi(api => api.getStatus());

  // Rail flyouts/tooltips and hidden labels depend on this.
  const railMode = !isPinned && !isMobile;

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Cluster' } })
      .then(({ items }) => {
        if (!cancelled) {
          setClusters(items as ClusterEntity[]);
        }
      })
      .catch(() => {
        // The sidebar stays usable without clusters.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  const routes = useMemo(() => {
    const resolve = (routeRef: Parameters<typeof routeResolutionApi.resolve>[0], fallback: string) => {
      try {
        return routeResolutionApi.resolve(routeRef)?.() ?? fallback;
      } catch {
        return fallback;
      }
    };
    return {
      notifications: resolve(notificationsPlugin.routes.root, '/notifications'),
      devTools: resolve(devToolsPlugin.routes.root, '/devtools'),
      settings: resolve(userSettingsPlugin.routes.settingsPage, '/settings'),
    };
    // Route paths are static for the lifetime of the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unreadCount = notificationsStatus.error
    ? undefined
    : notificationsStatus.value?.unread;

  const isMac =
    typeof window !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
  const modKey = isMac ? '⌘' : 'Ctrl';

  const switcherTargets = useMemo<SwitcherTarget[]>(() => {
    const pages: SwitcherTarget[] = [
      {
        id: 'page-home',
        group: 'Pages',
        label: 'Home',
        href: '/',
        icon: <HomeIcon size={16} />,
      },
      {
        id: 'page-notifications',
        group: 'Pages',
        label: 'Notifications',
        sub: unreadCount ? `${unreadCount} unread` : undefined,
        href: routes.notifications,
        icon: <BellIcon size={16} />,
      },
      {
        id: 'page-help',
        group: 'Pages',
        label: 'Help',
        href: HELP_HREF,
        icon: <BookIcon size={16} />,
      },
    ];
    if (admin.allowed) {
      pages.push(
        {
          id: 'page-admin',
          group: 'Pages',
          label: 'Admin',
          href: ADMIN_HREF,
          icon: <ShieldIcon size={16} />,
        },
        {
          id: 'page-devtools',
          group: 'Pages',
          label: 'DevTools',
          href: routes.devTools,
          icon: <TerminalIcon size={16} />,
        },
      );
    }
    pages.push({
      id: 'page-settings',
      group: 'Pages',
      label: 'Settings',
      href: routes.settings,
      icon: <GearIcon size={16} />,
    });

    const projectTargets: SwitcherTarget[] = projects.map(project => ({
      id: `project-${project.metadata.name}`,
      group: 'Projects',
      label: project.metadata.title ?? project.metadata.name,
      href: entityHref(project, 'domain'),
      icon: <DomainIcon size={16} />,
    }));

    const accountTargets: SwitcherTarget[] = accounts
      .map(account => {
        const project = projects.find(
          candidate => candidate.metadata.name === account.spec?.domain,
        );
        return {
          id: `account-${account.metadata.name}`,
          group: 'Accounts',
          label: account.metadata.name,
          sub: project?.metadata.title ?? account.spec?.domain,
          href: entityHref(account, 'account'),
          icon: <DomainIcon size={16} />,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return [
      ...pages,
      ...projectTargets,
      ...accountTargets,
      ...clusters.map(cluster => ({
        id: `cluster-${cluster.metadata.name}`,
        group: 'Clusters',
        label: cluster.metadata.name,
        sub: cluster.spec?.domain,
        href: entityHref(cluster, 'cluster'),
        icon: <BoxesIcon size={16} />,
      })),
    ];
  }, [admin.allowed, unreadCount, routes, projects, accounts, clusters]);

  // Global hotkeys: ⌘K / Ctrl+K opens the quick switcher, ⌘B / Ctrl+B
  // toggles the rail. Ignored while typing in a text field.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key !== 'k' && key !== 'b') {
        return;
      }
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (typing && key === 'b') {
        return;
      }
      event.preventDefault();
      if (key === 'k') {
        setSwitcherOpen(open => !open);
      } else {
        toggleSidebarPinState();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [toggleSidebarPinState]);

  // Roving focus over the visible nav rows: ↑/↓ moves, Enter activates.
  // Attached imperatively — a keydown prop on <nav> trips the a11y lint
  // rule for non-interactive elements.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }
      const items = Array.from(
        nav.querySelectorAll<HTMLElement>('a[href], button'),
      ).filter(el => el.offsetParent !== null);
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);
      if (currentIndex === -1) {
        return;
      }
      event.preventDefault();
      const next =
        event.key === 'ArrowDown'
          ? Math.min(currentIndex + 1, items.length - 1)
          : Math.max(currentIndex - 1, 0);
      items[next].focus();
    };
    nav.addEventListener('keydown', onKeyDown);
    return () => nav.removeEventListener('keydown', onKeyDown);
  }, [isMobile]);

  if (isMobile) {
    // Minimal mobile fallback (bottom bar); the mockup targets desktop.
    return (
      <>
        <nav className={styles.mobileBar} aria-label="Main">
          <Link
            to="/"
            className={styles.mobileItem}
            aria-current={isSamePage(pathname, '/') ? 'page' : undefined}
          >
            <HomeIcon />
          </Link>
          <button
            type="button"
            className={styles.mobileItem}
            aria-label="Search"
            onClick={() => setSwitcherOpen(true)}
          >
            <SearchIcon />
          </button>
          <Link
            to={routes.notifications}
            className={styles.mobileItem}
            aria-current={
              isSamePage(pathname, routes.notifications) ? 'page' : undefined
            }
          >
            <BellIcon />
          </Link>
          <Link
            to={HELP_HREF}
            className={styles.mobileItem}
            aria-current={isSamePage(pathname, HELP_HREF) ? 'page' : undefined}
          >
            <BookIcon />
          </Link>
        </nav>
        <QuickSwitcher
          open={switcherOpen}
          onClose={() => setSwitcherOpen(false)}
          targets={switcherTargets}
        />
      </>
    );
  }

  return (
    <>
      <nav
        ref={navRef}
        className={`${styles.shell} ${railMode ? styles.rail : ''}`}
        aria-label="Main"
      >
        <div className={styles.head}>
          <Link to="/" className={styles.logo} aria-label="Home">
            <span className={styles.mark}>
              <LogoIcon size={19} />
            </span>
            <span className={`${styles.word} ${styles.lbl}`}>Backstage</span>
          </Link>
          <TooltipTrigger>
            <ButtonIcon
              icon={<PanelLeftIcon size={18} />}
              variant="tertiary"
              aria-label={railMode ? 'Expand menu' : 'Collapse menu'}
              className={styles.collapse}
              onClick={toggleSidebarPinState}
            />
            <Tooltip>{`${railMode ? 'Expand' : 'Collapse'} (${modKey} B)`}</Tooltip>
          </TooltipTrigger>
        </div>

        <button
          type="button"
          className={styles.search}
          onClick={() => setSwitcherOpen(true)}
        >
          <SearchIcon size={16} />
          <span className={styles.lbl}>Search…</span>
          <span className={`${styles.kbd} ${styles.lbl}`}>{`${modKey} K`}</span>
        </button>

        <div className={styles.scroll}>
          <div role="group" aria-label="General">
            <NavItemLink
              href="/"
              icon={<HomeIcon />}
              label="Home"
              active={isSamePage(pathname, '/')}
              tooltip="Home"
            />
            <NavItemLink
              href={routes.notifications}
              icon={<BellIcon />}
              label="Notifications"
              active={isSamePage(pathname, routes.notifications)}
              tooltip="Notifications"
              end={
                unreadCount ? (
                  <span className={styles.badgeCnt}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                ) : undefined
              }
            />
          </div>

          <hr className={styles.div} />

          <div className={styles.sec} id="sb-sec-projects">
            Projects
            <span className={styles.cnt}>{projects.length}</span>
          </div>
          <ProjectsSection clusters={clusters} railMode={railMode} />
        </div>

        <hr className={styles.div} />

        <div className={styles.foot}>
          <div role="group" aria-label="Utility">
            <NavItemLink
              href={HELP_HREF}
              icon={<BookIcon />}
              label="Help"
              active={isSamePage(pathname, HELP_HREF)}
              tooltip="Help"
            />
            {admin.allowed && (
              <>
                <NavItemLink
                  href={ADMIN_HREF}
                  icon={<ShieldIcon />}
                  label="Admin"
                  active={isSamePage(pathname, ADMIN_HREF)}
                  tooltip="Admin"
                />
                <NavItemLink
                  href={routes.devTools}
                  icon={<TerminalIcon />}
                  label="DevTools"
                  active={isSamePage(pathname, routes.devTools)}
                  tooltip="DevTools"
                />
              </>
            )}
          </div>
          <UserMenu settingsHref={routes.settings} />
        </div>
      </nav>
      <QuickSwitcher
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        targets={switcherTargets}
      />
    </>
  );
}

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: () => <Sidebar />,
  },
});
